// src/components/business/BusinessDomainsTab.tsx
//
// DEM-2121 F5 — sub-aba "Domínios" das Configurações do business.
//
// Cadastra N domínios por business, marca um como primário, exibe o registro TXT
// esperado em _raios-verification.{domain} com instruções e um botão "verificar
// agora" que consulta o DNS real. Status por domínio (pending/verified/failed/
// expired) e, no fail, a mensagem explícita "esperava X em Y, encontrei Z".
//
// Dados LAZY: busca quando a sub-aba monta. Segue o padrão de BusinessReposTab.

import { useEffect, useState } from 'react';
import {
  api,
  type BusinessDomain,
  type DomainVerificationStatus,
} from '@/lib/api';
import { BizModal } from './BizModal';
import { WfButton, WfBadge, WfEmpty, type WfBadgeVariant } from '@/components/wf';
import { Globe, Plus, Trash2, Star, RefreshCw, Copy, Check } from 'lucide-react';

interface BusinessDomainsTabProps {
  businessId: string;
}

const STATUS_LABEL: Record<DomainVerificationStatus, string> = {
  pending: 'Pendente',
  verified: 'Verificado',
  failed: 'Falhou',
  expired: 'Expirado',
};

const STATUS_VARIANT: Record<DomainVerificationStatus, WfBadgeVariant> = {
  pending: 'amber',
  verified: 'success',
  failed: 'danger',
  expired: 'neutral',
};

export function BusinessDomainsTab({ businessId }: BusinessDomainsTabProps) {
  const [domains, setDomains] = useState<BusinessDomain[] | null>(null);
  const [listMsg, setListMsg] = useState('');

  // Modal "Adicionar domínio"
  const [showAdd, setShowAdd] = useState(false);
  const [addDomain, setAddDomain] = useState('');
  const [addPrimary, setAddPrimary] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addMsg, setAddMsg] = useState('');

  // Estado por-domínio: verificação em andamento + última mensagem de verify.
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [verifyMsg, setVerifyMsg] = useState<Record<string, { ok: boolean; text: string }>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // LAZY: busca quando a sub-aba monta.
  useEffect(() => {
    let alive = true;
    api.listBusinessDomains(businessId)
      .then((ds) => { if (alive) setDomains(ds); })
      .catch((err) => {
        if (alive) {
          setDomains([]);
          setListMsg(err instanceof Error ? err.message : 'Erro ao carregar domínios');
        }
      });
    return () => { alive = false; };
  }, [businessId]);

  const resetAddForm = () => {
    setAddDomain('');
    setAddPrimary(false);
    setAddMsg('');
  };

  const handleAdd = async () => {
    const domain = addDomain.trim().toLowerCase();
    if (!domain) return;
    setAddLoading(true);
    setAddMsg('');
    try {
      const created = await api.addBusinessDomain(businessId, { domain, is_primary: addPrimary });
      setDomains((prev) => {
        // Se este virou primário, desmarca os demais no estado local também.
        const base = addPrimary ? (prev ?? []).map((d) => ({ ...d, is_primary: false })) : (prev ?? []);
        return [...base, created];
      });
      setShowAdd(false);
      resetAddForm();
    } catch (err) {
      setAddMsg(err instanceof Error ? err.message : 'Erro ao adicionar domínio');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async (domainId: string) => {
    setListMsg('');
    try {
      await api.deleteBusinessDomain(businessId, domainId);
      setDomains((prev) => (prev ?? []).filter((d) => d.id !== domainId));
    } catch (err) {
      setListMsg(err instanceof Error ? err.message : 'Erro ao remover domínio');
    }
  };

  const handleSetPrimary = async (domainId: string) => {
    setListMsg('');
    try {
      const updated = await api.updateBusinessDomain(businessId, domainId, { is_primary: true });
      setDomains((prev) =>
        (prev ?? []).map((d) =>
          d.id === updated.id ? updated : { ...d, is_primary: false },
        ),
      );
    } catch (err) {
      setListMsg(err instanceof Error ? err.message : 'Erro ao marcar como primário');
    }
  };

  const handleVerify = async (domainId: string) => {
    setVerifyingId(domainId);
    setVerifyMsg((prev) => ({ ...prev, [domainId]: { ok: false, text: '' } }));
    try {
      const result = await api.verifyBusinessDomain(businessId, domainId);
      setDomains((prev) => (prev ?? []).map((d) => (d.id === domainId ? result.domain : d)));
      setVerifyMsg((prev) => ({ ...prev, [domainId]: { ok: result.verified, text: result.message } }));
    } catch (err) {
      setVerifyMsg((prev) => ({
        ...prev,
        [domainId]: { ok: false, text: err instanceof Error ? err.message : 'Erro ao verificar' },
      }));
    } finally {
      setVerifyingId(null);
    }
  };

  const handleCopy = async (domainId: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(domainId);
      setTimeout(() => setCopiedId((cur) => (cur === domainId ? null : cur)), 1500);
    } catch {
      /* clipboard indisponível — silencioso */
    }
  };

  return (
    <div className="wf-bizpanel">
      {/* Header (padrão .bizpanel-h + .bpcount + .dnewbtn) */}
      <div className="wf-bizpanel-h">
        <span>
          Domínios <span className="wf-bpcount">{domains?.length ?? '…'}</span>
        </span>
        <button type="button" className="wf-dnewbtn" style={{ margin: 0 }} onClick={() => setShowAdd(true)}>
          <Plus className="ic" aria-hidden="true" />
          Adicionar domínio
        </button>
      </div>

      {listMsg && <p className="text-xs mb-2" style={{ color: 'var(--wf-danger-text)' }}>{listMsg}</p>}

      {domains === null ? (
        <div className="wf-repolist" aria-busy="true">
          {[1, 2].map((i) => (
            <div key={i} className="wf-repocard" style={{ minHeight: 56 }} />
          ))}
        </div>
      ) : domains.length === 0 ? (
        <WfEmpty icon={<Globe />}>
          Nenhum domínio cadastrado — adicione o site do seu negócio e verifique-o
          via DNS TXT para que os agentes possam navegá-lo com confiança.
        </WfEmpty>
      ) : (
        <div className="wf-repolist">
          {domains.map((d) => {
            const vm = verifyMsg[d.id];
            return (
              <div key={d.id} className="wf-repocard" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="rico">
                    <Globe className="ic" aria-hidden="true" />
                  </div>
                  <div className="rinfo" style={{ flex: 1 }}>
                    <div className="rname" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {d.domain}
                      {d.is_primary && (
                        <WfBadge variant="accent" title="Domínio primário">Primário</WfBadge>
                      )}
                    </div>
                    <div className="rurl">
                      <WfBadge variant={STATUS_VARIANT[d.verification_status]}>
                        {STATUS_LABEL[d.verification_status]}
                      </WfBadge>
                      {d.last_checked_at && (
                        <span className="text-xs" style={{ marginLeft: 8, opacity: 0.7 }}>
                          verificado por último em {new Date(d.last_checked_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flex: '0 0 auto' }}>
                    {!d.is_primary && (
                      <button
                        type="button"
                        className="wf-iconbtn"
                        aria-label="Marcar como primário"
                        title="Marcar como primário"
                        onClick={() => void handleSetPrimary(d.id)}
                      >
                        <Star aria-hidden="true" />
                      </button>
                    )}
                    <button
                      type="button"
                      className="wf-iconbtn wf-iconbtn--no"
                      aria-label="Remover domínio"
                      title="Remover domínio"
                      onClick={() => void handleDelete(d.id)}
                    >
                      <Trash2 aria-hidden="true" />
                    </button>
                  </div>
                </div>

                {/* Instruções do registro TXT esperado. */}
                <div
                  style={{
                    background: 'var(--wf-surface-2, rgba(0,0,0,0.04))',
                    borderRadius: 8,
                    padding: '10px 12px',
                    fontSize: 12,
                  }}
                >
                  <p style={{ marginBottom: 6, opacity: 0.85 }}>
                    Crie um registro <strong>TXT</strong> no DNS do seu domínio para verificá-lo:
                  </p>
                  <div className="wf-nc-2col" style={{ gap: 8, marginBottom: 6 }}>
                    <div className="wf-field" style={{ margin: 0 }}>
                      <span className="wf-label">Host / Nome</span>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <code style={{ flex: 1, wordBreak: 'break-all' }}>{d.verification_host}</code>
                        <button
                          type="button"
                          className="wf-iconbtn"
                          aria-label="Copiar host"
                          title="Copiar host"
                          onClick={() => void handleCopy(`${d.id}-host`, d.verification_host)}
                        >
                          {copiedId === `${d.id}-host` ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                        </button>
                      </div>
                    </div>
                    <div className="wf-field" style={{ margin: 0 }}>
                      <span className="wf-label">Valor (TXT)</span>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <code style={{ flex: 1, wordBreak: 'break-all' }}>{d.txt_record}</code>
                        <button
                          type="button"
                          className="wf-iconbtn"
                          aria-label="Copiar valor"
                          title="Copiar valor"
                          onClick={() => void handleCopy(`${d.id}-val`, d.txt_record)}
                        >
                          {copiedId === `${d.id}-val` ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <WfButton
                      variant="primary"
                      onClick={() => void handleVerify(d.id)}
                      disabled={verifyingId === d.id}
                    >
                      <RefreshCw className="ic" aria-hidden="true" />
                      {verifyingId === d.id ? 'Verificando…' : 'Verificar agora'}
                    </WfButton>
                    {vm && vm.text && (
                      <span
                        className="text-xs"
                        style={{ color: vm.ok ? 'var(--wf-success-text)' : 'var(--wf-danger-text)' }}
                      >
                        {vm.text}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal: Adicionar domínio */}
      <BizModal
        open={showAdd}
        onClose={() => { setShowAdd(false); setAddMsg(''); }}
        title="Adicionar domínio"
        icon={<Globe className="ic" aria-hidden="true" />}
        footer={
          <>
            <WfButton variant="ghost" onClick={() => setShowAdd(false)}>Cancelar</WfButton>
            <WfButton variant="primary" disabled={addLoading || !addDomain.trim()} onClick={handleAdd}>
              {addLoading ? 'Adicionando…' : 'Adicionar'}
            </WfButton>
          </>
        }
      >
        <div className="wf-field">
          <label className="wf-label" htmlFor="domain-name">Domínio</label>
          <input
            id="domain-name"
            className="wf-input"
            value={addDomain}
            onChange={(e) => setAddDomain(e.target.value)}
            placeholder="raios.app"
          />
        </div>
        <div className="wf-field">
          <label className="wf-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={addPrimary}
              onChange={(e) => setAddPrimary(e.target.checked)}
            />
            Marcar como domínio primário
          </label>
        </div>
        {addMsg && <p className="text-xs" style={{ color: 'var(--wf-danger-text)' }}>{addMsg}</p>}
      </BizModal>
    </div>
  );
}
